import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch } from '../utils/apiUtils';
import { formatDateOnly } from '../utils/dateUtils';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children, currentUser }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastNotifications, setToastNotifications] = useState([]);
  const [lastCheckTime, setLastCheckTime] = useState(() => {
    // Cargar última vez que se revisaron las notificaciones
    const saved = localStorage.getItem(`lastNotificationCheck_${currentUser?.id}`);
    return saved ? new Date(saved) : new Date();
  });

  // Función para cargar notificaciones
  const loadNotifications = async () => {
    if (!currentUser) return;

    try {
      console.log('🔔 Cargando notificaciones para usuario:', currentUser.firstName, currentUser.lastName);
      
      // Cargar tareas del servidor
      const tasksResponse = await apiFetch('/tasks');
      if (!tasksResponse.ok) {
        console.error('Error al cargar tareas para notificaciones');
        return;
      }

      const allTasks = await tasksResponse.json();
      console.log('📋 Tareas cargadas para notificaciones:', allTasks.length);

      // Filtrar tareas asignadas al usuario actual
      const currentUserName = `${currentUser.firstName} ${currentUser.lastName}`;
      const userTasks = allTasks.filter(task => task.assignedTo === currentUserName);
      console.log('👤 Tareas del usuario actual:', userTasks.length);

      // Obtener tareas nuevas desde la última revisión
      const newTasks = userTasks.filter(task => {
        const taskDate = new Date(task.createdAt || task.updatedAt);
        return taskDate > lastCheckTime;
      });

      console.log('🆕 Tareas nuevas desde última revisión:', newTasks.length);

      // Cargar comentarios para las tareas del usuario
      const newComments = [];
      for (const task of userTasks) {
        try {
          const commentsResponse = await apiFetch(`/tasks/${task.id}/comments`);
          if (commentsResponse.ok) {
            const taskComments = await commentsResponse.json();
            
            // Filtrar comentarios nuevos que no sean del usuario actual
            const taskNewComments = taskComments.filter(comment => {
              const commentDate = new Date(comment.createdAt || comment.created_at);
              const commentUser = comment.userName || comment.user_name;
              
              // Solo comentarios nuevos que no sean del usuario actual
              return commentDate > lastCheckTime && commentUser !== currentUserName;
            });
            
            taskNewComments.forEach(comment => {
              newComments.push({
                ...comment,
                taskId: task.id,
                taskDescription: task.description,
                clientName: task.clientName || `Cliente ${task.clientId}`
              });
            });
          }
        } catch (error) {
          console.error(`Error loading comments for task ${task.id}:`, error);
        }
      }

      console.log('💬 Comentarios nuevos:', newComments.length);

      // Crear notificaciones de tareas
      const taskNotifications = newTasks.map(task => ({
        id: `task-${task.id}`,
        type: 'new_task',
        title: 'Nueva tarea asignada',
        message: `Se te ha asignado una nueva tarea: ${task.description}`,
        clientName: task.clientName || `Cliente ${task.clientId}`,
        taskId: task.id,
        priority: task.priority,
        createdAt: task.createdAt || task.updatedAt,
        isRead: false
      }));

      // Crear notificaciones de comentarios
      const commentNotifications = newComments.map(comment => ({
        id: `comment-${comment.id}`,
        type: 'new_comment',
        title: 'Nuevo comentario en tu tarea',
        message: `${comment.userName || comment.user_name} comentó en: ${comment.taskDescription}`,
        clientName: comment.clientName,
        taskId: comment.taskId,
        commentText: comment.comment,
        createdAt: comment.createdAt || comment.created_at,
        isRead: false
      }));

      // Combinar y ordenar notificaciones
      const allNotifications = [...taskNotifications, ...commentNotifications]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      console.log('🔔 Total de notificaciones generadas:', allNotifications.length);

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.length);

    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Función para marcar notificaciones como leídas
  const markNotificationsAsRead = () => {
    const now = new Date();
    localStorage.setItem(`lastNotificationCheck_${currentUser?.id}`, now.toISOString());
    setLastCheckTime(now);
    setUnreadCount(0);
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
    console.log('✅ Notificaciones marcadas como leídas');
  };

  // Función para ir a una tarea específica desde notificación
  const goToTask = (taskId) => {
    // Buscar la tarea y hacer scroll hacia ella
    const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);
    if (taskElement) {
      taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      taskElement.classList.add('highlight-task');
      setTimeout(() => {
        taskElement.classList.remove('highlight-task');
      }, 3000);
    }
  };

  // Función para obtener el emoji de prioridad
  const getPriorityEmoji = (priority) => {
    switch (priority) {
      case 'urgente': return '🔴';
      case 'alta': return '🟠';
      case 'media': return '🟡';
      case 'baja': return '🟢';
      default: return '🟡';
    }
  };

  // Función para mostrar notificaciones toast
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    const newToast = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      timestamp: new Date()
    };

    setToastNotifications(prev => [...prev, newToast]);

    // Auto-remover después de 5 segundos
    setTimeout(() => {
      setToastNotifications(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };

  // Función para remover una notificación toast específica
  const removeToastNotification = (id) => {
    setToastNotifications(prev => prev.filter(toast => toast.id !== id));
  };

  // Cargar notificaciones cuando cambia el usuario
  useEffect(() => {
    if (currentUser) {
      loadNotifications();
    }
  }, [currentUser, lastCheckTime]);

  // Cargar notificaciones periódicamente (cada 30 segundos)
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      loadNotifications();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [currentUser, lastCheckTime]);

  const value = {
    notifications,
    unreadCount,
    toastNotifications,
    loadNotifications,
    markNotificationsAsRead,
    goToTask,
    getPriorityEmoji,
    formatDateOnly,
    showNotification,
    removeToastNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
